import type { ReactNode } from "react"
import { Alert, Button, Stack, Typography, styled } from "@mui/material"
import type { AuthMessage } from "../functions/authMessage"
import googleIcon from "../img/google-official.svg"
import microsoftIcon from "../img/microsoft.svg"
import LogoCard from "./LogoCard"

const ProviderButton = styled(Button)(({ theme }) => ({
  width: "100%",
  minHeight: 42,
  padding: theme.spacing(2, 4),
  border: "1px solid #dadce0",
  borderRadius: 4,
  backgroundColor: "#fff",
  color: "#444",
  boxShadow: "0 2px 4px rgb(0 0 0 / 20%)",
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  "&:hover, &:focus": {
    backgroundColor: "#f8f8f8",
    boxShadow: "0 2px 6px rgb(0 0 0 / 28%)",
  },
}))

const ProviderIcon = styled("img")({
  display: "block",
  width: 20,
  height: 20,
  objectFit: "contain",
})

export interface LoginCardProps {
  apiUrl: string
  authMessage?: AuthMessage | null
  className?: string
  title?: ReactNode
  description?: ReactNode
  maintenanceMode?: boolean
  footer?: ReactNode
}

/**
 * Shared login card for MobileMind web apps. Authentication state and page-level
 * presentation stay in the consuming app. MUI's styled wrapper provides the
 * standard sx prop when a caller needs to compose the card into a page.
 */
export const LoginCard = styled(
  ({
    apiUrl,
    authMessage,
    className,
    description,
    footer,
    maintenanceMode = false,
    title,
  }: LoginCardProps) => {
    const normalizedApiUrl = apiUrl.replace(/\/$/, "")

    return (
      <LogoCard className={className}>
        <Stack spacing={4} alignItems="center">
          {(title || description) && (
            <Stack spacing={1} alignItems="center">
              {title && (
                <Typography variant="h6" component="h1" fontWeight={700}>
                  {title}
                </Typography>
              )}
              {description && (
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              )}
            </Stack>
          )}

          {authMessage && (
            <Alert severity={authMessage.severity} sx={{ width: "100%" }}>
              {authMessage.message}
            </Alert>
          )}

          {maintenanceMode ? (
            <Alert severity="warning" sx={{ textAlign: "left", width: "100%" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                MobileMind is currently under maintenance
              </Typography>
              <Typography variant="body2">
                Don&apos;t worry, we&apos;re working on it and will be right
                back!
              </Typography>
            </Alert>
          ) : (
            <Stack spacing={4}>
              <ProviderButton
                href={`${normalizedApiUrl}/user/login/google`}
                startIcon={<ProviderIcon src={googleIcon} alt="" />}
              >
                Sign in with Google
              </ProviderButton>
              <ProviderButton
                href={`${normalizedApiUrl}/user/login/entra`}
                startIcon={<ProviderIcon src={microsoftIcon} alt="" />}
              >
                Sign in with Microsoft
              </ProviderButton>
            </Stack>
          )}
          {footer}
        </Stack>
      </LogoCard>
    )
  }
)({})

export default LoginCard
